const QUEUE_STORAGE_KEY = 'traveldesk_booking_funnel_queue_v2';
const DELIVERED_STORAGE_KEY = 'traveldesk_booking_funnel_delivered_v2';
const SESSION_STORAGE_PREFIX = 'traveldesk_booking_funnel';
const MAX_QUEUE_ITEMS = 64;
const MAX_DELIVERED_ITEMS = 160;
const ITEM_TTL_MS = 72 * 60 * 60 * 1000;
const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;

const memorySessions = new Map();
const memoryCompletedSessions = new Set();
const memoryBackedStorageKeys = new Set();
let memoryQueue = [];
let memoryDelivered = [];
let flushPromise = null;
let flushTimer = null;
let started = false;

function isBrowser() {
  return typeof window !== 'undefined';
}

function normalizeCompanyId(companyId) {
  const value = Number(companyId);
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function normalizeBookingFunnelIndustry(industry) {
  const value = String(industry || '').trim().toLowerCase();
  const aliases = {
    transportation: 'transportation',
    transport: 'transportation',
    transpo: 'transportation',
    tours: 'tours',
    tour: 'tours',
    activities: 'tours',
    activity: 'tours',
    restaurant: 'restaurant',
    restaurants: 'restaurant',
  };

  return aliases[value] || '';
}

function safeUuid() {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `bf_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

function hash32(value, seed) {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function stableEventId(signature) {
  const hex = [
    hash32(signature, 2166136261),
    hash32(signature, 2246822507),
    hash32(signature, 3266489909),
    hash32(signature, 668265263),
  ].join('').split('');

  hex[12] = '5';
  hex[16] = ((parseInt(hex[16], 16) & 3) | 8).toString(16);
  const value = hex.join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20, 32)}`;
}

function readStorageArray(storageName, key, fallback) {
  const fallbackCopy = Array.isArray(fallback) ? fallback.slice() : [];
  if (!isBrowser()) return fallbackCopy;

  const fallbackKey = `${storageName}:${key}`;
  if (memoryBackedStorageKeys.has(fallbackKey)) return fallbackCopy;

  try {
    const raw = window[storageName]?.getItem(key);
    if (!raw) return fallbackCopy;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallbackCopy;
  } catch (error) {
    memoryBackedStorageKeys.add(fallbackKey);
    return fallbackCopy;
  }
}

function writeStorageArray(storageName, key, value, setFallback) {
  const valueCopy = Array.isArray(value) ? value.slice() : [];
  setFallback(valueCopy);
  if (!isBrowser()) {
    return;
  }

  const fallbackKey = `${storageName}:${key}`;
  try {
    const storage = window[storageName];
    if (!storage) throw new Error('Storage is unavailable.');
    storage.setItem(key, JSON.stringify(valueCopy));
    memoryBackedStorageKeys.delete(fallbackKey);
  } catch (error) {
    memoryBackedStorageKeys.add(fallbackKey);
  }
}

function isUnexpired(item, now = Date.now()) {
  const expiresAt = Date.parse(item?.expires_at || '');
  return Number.isFinite(expiresAt) && expiresAt > now;
}

function readQueue() {
  const stored = readStorageArray('localStorage', QUEUE_STORAGE_KEY, memoryQueue);
  const entries = stored
    .filter((item) => item?.event_id && item?.payload && isUnexpired(item))
    .slice(-MAX_QUEUE_ITEMS);
  if (entries.length !== stored.length) writeQueue(entries);
  return entries;
}

function writeQueue(entries) {
  const cleaned = entries
    .filter((item) => item?.event_id && item?.payload && isUnexpired(item))
    .slice(-MAX_QUEUE_ITEMS);
  writeStorageArray('localStorage', QUEUE_STORAGE_KEY, cleaned, (value) => {
    memoryQueue = value;
  });
}

function readDelivered() {
  const stored = readStorageArray('localStorage', DELIVERED_STORAGE_KEY, memoryDelivered);
  const entries = stored
    .filter((item) => item?.signature && item?.event_id && isUnexpired(item))
    .slice(-MAX_DELIVERED_ITEMS);
  if (entries.length !== stored.length) writeDelivered(entries);
  return entries;
}

function writeDelivered(entries) {
  const cleaned = entries
    .filter((item) => item?.signature && item?.event_id && isUnexpired(item))
    .slice(-MAX_DELIVERED_ITEMS);
  writeStorageArray('localStorage', DELIVERED_STORAGE_KEY, cleaned, (value) => {
    memoryDelivered = value;
  });
}

function isDelivered(signature) {
  return readDelivered().some((item) => item.signature === signature);
}

function markDelivered(entry) {
  const deliveredAt = new Date().toISOString();
  const delivered = readDelivered().filter((item) => item.signature !== entry.signature);
  delivered.push({
    signature: entry.signature,
    event_id: entry.event_id,
    delivered_at: deliveredAt,
    expires_at: new Date(Date.now() + ITEM_TTL_MS).toISOString(),
  });
  writeDelivered(delivered);
  writeQueue(readQueue().filter((item) => item.event_id !== entry.event_id));
}

function markAttemptFailed(entry) {
  const attempts = Number(entry.attempts || 0) + 1;
  const baseDelay = Math.min(MAX_RETRY_DELAY_MS, 2500 * (2 ** Math.min(attempts - 1, 7)));
  const retryDelay = baseDelay + Math.floor(Math.random() * 750);
  const queue = readQueue().map((item) => (
    item.event_id === entry.event_id
      ? {
        ...item,
        attempts,
        next_attempt_at: new Date(Date.now() + retryDelay).toISOString(),
      }
      : item
  ));
  writeQueue(queue);
}

function funnelApiUrl() {
  const base = String(process.env.VUE_APP_BASEURL || '').replace(/\/+$/, '');
  return `${base}/public/booking-funnel/visits`;
}

function nextPendingDelay() {
  const queue = readQueue();
  if (!queue.length) return null;
  const nextAt = Math.min(...queue.map((item) => Date.parse(item.next_attempt_at || '') || 0));
  return Math.max(0, nextAt - Date.now());
}

function scheduleFlush(delay = 0) {
  if (!isBrowser()) return;
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushBookingFunnelQueue();
  }, Math.min(Math.max(0, delay), MAX_RETRY_DELAY_MS));
}

function normalizeBookingStep(step, industry) {
  const number = Number(step?.number);
  const key = String(step?.key || '').trim().toLowerCase().slice(0, 64);
  if (!key || !Number.isInteger(number) || number < 1 || number > 8) return null;

  return {
    key,
    number,
    industry,
    label: String(step?.label || step?.label_en || key).slice(0, 120),
    label_en: String(step?.label_en || step?.label || key).slice(0, 120),
    label_es: String(step?.label_es || step?.label || key).slice(0, 120),
  };
}

export function getBookingFunnelSessionKey(companyId, industry) {
  const normalizedCompanyId = normalizeCompanyId(companyId);
  const normalizedIndustry = normalizeBookingFunnelIndustry(industry);
  if (!normalizedCompanyId || !normalizedIndustry) return '';

  const storageKey = `${SESSION_STORAGE_PREFIX}_${normalizedCompanyId}_${normalizedIndustry}`;
  if (memorySessions.has(storageKey)) return memorySessions.get(storageKey);

  if (isBrowser()) {
    try {
      const existing = window.sessionStorage?.getItem(storageKey);
      if (existing) {
        memorySessions.set(storageKey, existing);
        return existing;
      }
    } catch (error) {
      // The in-memory fallback keeps the current page functional.
    }
  }

  const sessionKey = safeUuid();
  memorySessions.set(storageKey, sessionKey);
  if (isBrowser()) {
    try {
      window.sessionStorage?.setItem(storageKey, sessionKey);
    } catch (error) {
      // The session remains available in memory for this page.
    }
  }

  return sessionKey;
}

function completionStorageKey(companyId, industry) {
  return `${SESSION_STORAGE_PREFIX}_completed_${companyId}_${industry}`;
}

function wasSessionCompleted(companyId, industry) {
  const key = completionStorageKey(companyId, industry);
  if (memoryCompletedSessions.has(key)) return true;
  if (!isBrowser()) return false;

  try {
    const completed = window.sessionStorage?.getItem(key) === '1';
    if (completed) memoryCompletedSessions.add(key);
    return completed;
  } catch (error) {
    return false;
  }
}

function markSessionCompleted(companyId, industry) {
  const key = completionStorageKey(companyId, industry);
  memoryCompletedSessions.add(key);
  if (!isBrowser()) return;

  try {
    window.sessionStorage?.setItem(key, '1');
  } catch (error) {
    // The in-memory marker still protects this page lifecycle.
  }
}

export function rotateBookingFunnelSession(companyId, industry) {
  const normalizedCompanyId = normalizeCompanyId(companyId);
  const normalizedIndustry = normalizeBookingFunnelIndustry(industry);
  if (!normalizedCompanyId || !normalizedIndustry) return '';

  const storageKey = `${SESSION_STORAGE_PREFIX}_${normalizedCompanyId}_${normalizedIndustry}`;
  const completedKey = completionStorageKey(normalizedCompanyId, normalizedIndustry);
  const sessionKey = safeUuid();
  memorySessions.set(storageKey, sessionKey);
  memoryCompletedSessions.delete(completedKey);

  if (isBrowser()) {
    try {
      window.sessionStorage?.setItem(storageKey, sessionKey);
      window.sessionStorage?.removeItem(completedKey);
    } catch (error) {
      // The rotated session remains available in memory for this page.
    }
  }

  return sessionKey;
}

function sessionKeyForStep(companyId, industry, step) {
  let sessionKey;
  if (step.number === 1 && step.key !== 'confirmation' && wasSessionCompleted(companyId, industry)) {
    sessionKey = rotateBookingFunnelSession(companyId, industry);
  } else {
    sessionKey = getBookingFunnelSessionKey(companyId, industry);
  }

  if (step.key === 'confirmation') markSessionCompleted(companyId, industry);
  return sessionKey;
}

export function withBookingFunnelSessionKey(payload, companyId, industry) {
  const sessionKey = getBookingFunnelSessionKey(companyId, industry);
  return sessionKey
    ? { ...payload, funnel_session_key: sessionKey }
    : { ...payload };
}

export function trackBookingFunnelStep({ companyId, industry, bookingStep, pageContext = {} }) {
  const normalizedCompanyId = normalizeCompanyId(companyId);
  const normalizedIndustry = normalizeBookingFunnelIndustry(industry);
  const step = normalizeBookingStep(bookingStep, normalizedIndustry);
  if (!normalizedCompanyId || !normalizedIndustry || !step) {
    return Promise.resolve({ queued: false, reason: 'invalid_context' });
  }

  const sessionKey = step
    ? sessionKeyForStep(normalizedCompanyId, normalizedIndustry, step)
    : '';
  if (!sessionKey) {
    return Promise.resolve({ queued: false, reason: 'invalid_context' });
  }

  const signature = `${normalizedCompanyId}|${normalizedIndustry}|${sessionKey}|step_viewed|${step.key}|${pageContext.source_url || ''}`;
  const eventId = stableEventId(signature);
  if (isDelivered(signature)) {
    return Promise.resolve({ queued: false, delivered: true, event_id: eventId });
  }

  const queue = readQueue();
  if (!queue.some((item) => item.event_id === eventId)) {
    const occurredAt = new Date().toISOString();
    queue.push({
      signature,
      event_id: eventId,
      attempts: 0,
      created_at: occurredAt,
      expires_at: new Date(Date.now() + ITEM_TTL_MS).toISOString(),
      next_attempt_at: occurredAt,
      payload: {
        route_name: pageContext.route_name || null,
        route_path: pageContext.route_path || null,
        source_url: pageContext.source_url || null,
        referrer: pageContext.referrer || null,
        locale: pageContext.locale || null,
        event_id: eventId,
        event_name: 'step_viewed',
        company_id: normalizedCompanyId,
        industry: normalizedIndustry,
        session_key: sessionKey,
        booking_step: step,
        at: occurredAt,
        occurred_at: occurredAt,
      },
    });
    writeQueue(queue);
  }

  return flushBookingFunnelQueue();
}

export function flushBookingFunnelQueue() {
  if (!isBrowser() || typeof window.fetch !== 'function') {
    return Promise.resolve({ delivered: 0, pending: readQueue().length });
  }
  if (window.navigator?.onLine === false) {
    scheduleFlush(5000);
    return Promise.resolve({ delivered: 0, pending: readQueue().length, offline: true });
  }
  if (flushPromise) return flushPromise;

  flushPromise = (async () => {
    let delivered = 0;
    const dueEntries = readQueue().filter((item) => {
      const nextAttemptAt = Date.parse(item.next_attempt_at || '') || 0;
      return nextAttemptAt <= Date.now();
    });

    for (const entry of dueEntries) {
      try {
        const response = await window.fetch(funnelApiUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(entry.payload),
          keepalive: true,
        });

        if (response.ok) {
          markDelivered(entry);
          delivered += 1;
        } else {
          markAttemptFailed(entry);
        }
      } catch (error) {
        markAttemptFailed(entry);
      }
    }

    return { delivered, pending: readQueue().length };
  })().finally(() => {
    flushPromise = null;
    const delay = nextPendingDelay();
    if (delay !== null) scheduleFlush(delay);
  });

  return flushPromise;
}

function sendPendingWithBeacon() {
  if (!isBrowser() || typeof window.navigator?.sendBeacon !== 'function') return;

  readQueue().forEach((entry) => {
    try {
      const body = new Blob([JSON.stringify(entry.payload)], { type: 'application/json' });
      window.navigator.sendBeacon(funnelApiUrl(), body);
    } catch (error) {
      // The durable queue remains pending and will retry with fetch later.
    }
  });
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') flushBookingFunnelQueue();
}

function handleStorage(event) {
  if (event.key === QUEUE_STORAGE_KEY) flushBookingFunnelQueue();
}

export function startBookingFunnelDelivery() {
  if (!isBrowser() || started) return;
  started = true;
  window.addEventListener('online', flushBookingFunnelQueue);
  window.addEventListener('pagehide', sendPendingWithBeacon);
  window.addEventListener('storage', handleStorage);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  flushBookingFunnelQueue();
}

export function stopBookingFunnelDelivery() {
  if (!isBrowser() || !started) return;
  started = false;
  window.removeEventListener('online', flushBookingFunnelQueue);
  window.removeEventListener('pagehide', sendPendingWithBeacon);
  window.removeEventListener('storage', handleStorage);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = null;
}
